/**
 * f12.2: the review.html "Live" alert, and a compact badge for the grid toolbar.
 *
 * Connected: a pulsing `.live-dot`, "Live — figures update for everyone
 * watching" and "Last update N seconds ago" from the last `pushed_at`, ticking
 * each second. Otherwise: "Reconnecting… figures may be out of date" in the
 * prototype's warning pill. No viewer count (Q-f12.2-3).
 */

import { useEffect, useState } from 'react';
import { Radio, WifiOff } from 'lucide-react';

import type { LiveState } from './useLiveCycle';

export const LIVE_TITLE = 'Live — figures update for everyone watching';
export const RECONNECTING = 'Reconnecting… figures may be out of date';
export const ACCESS_LOST = 'You no longer have access to this review';

/** "Last update just now", "… N seconds ago", "… N minutes ago". */
export function lastUpdateText(pushedAt: number | null, now: number): string {
  if (pushedAt === null) return 'Waiting for the first update';
  const seconds = Math.max(0, Math.floor((now - pushedAt) / 1000));
  if (seconds < 1) return 'Last update just now';
  if (seconds < 60) return `Last update ${seconds} ${seconds === 1 ? 'second' : 'seconds'} ago`;
  const minutes = Math.floor(seconds / 60);
  return `Last update ${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
}

function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  return now;
}

function CompactIndicator({ live, now }: { live: LiveState; now: number }) {
  if (live.status === 'closed') return <span className="badge pill-warning" role="status">{ACCESS_LOST}</span>;
  if (live.status === 'live') {
    return (
      <span className="badge pill-info live-badge" role="status" title={lastUpdateText(live.lastPushedAt, now)}>
        <span className="live-dot" aria-hidden="true" /> Live
      </span>
    );
  }
  return (
    <span className="badge pill-warning live-badge" role="status" title={RECONNECTING}>
      <WifiOff aria-hidden="true" /> Reconnecting…
    </span>
  );
}

export function LiveIndicator({ live, compact = false }: { live: LiveState; compact?: boolean }) {
  const now = useNow();
  if (compact) return <CompactIndicator live={live} now={now} />;

  if (live.status === 'closed') {
    return (
      <div className="alert alert-warning mb-4" role="status">
        <WifiOff aria-hidden="true" />
        <div className="live-alert-text">
          <div className="a-title">{ACCESS_LOST}</div>
          <div className="a-body">Figures on this page are no longer updated. Return to the cycle to continue.</div>
        </div>
      </div>
    );
  }

  const connected = live.status === 'live';
  return (
    <div className="alert alert-info mb-4" role="status" aria-live="polite">
      <Radio aria-hidden="true" />
      <div className="live-alert-text">
        <div className="a-title">
          <span className="live-title">
            <span className="live-dot" aria-hidden="true" /> {LIVE_TITLE}
          </span>
        </div>
        <div className="a-body">
          Every committed change recalculates in the background and lands here without a refresh.{' '}
          {lastUpdateText(live.lastPushedAt, now)}.
        </div>
      </div>
      {!connected && <span className="badge pill-warning live-alert-badge">{RECONNECTING}</span>}
    </div>
  );
}
